import React from 'react';
import { Sparkles, Play, Clock, Zap } from 'lucide-react';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Switch } from './ui/switch';
import { Slider } from './ui/slider';

export const ENTRANCE_ANIMATIONS = [
  { value: 'none', label: 'None' },
  { value: 'fadeIn', label: 'Fade In' },
  { value: 'fadeInUp', label: 'Fade In Up' },
  { value: 'fadeInDown', label: 'Fade In Down' },
  { value: 'fadeInLeft', label: 'Slide from Left' },
  { value: 'fadeInRight', label: 'Slide from Right' },
  { value: 'zoomIn', label: 'Zoom In' },
  { value: 'bounceIn', label: 'Bounce In' },
  { value: 'slideInUp', label: 'Slide Up' },
  { value: 'flipIn', label: 'Flip In' },
  { value: 'rotateIn', label: 'Rotate In' },
];

export const ATTENTION_ANIMATIONS = [
  { value: 'none', label: 'None' },
  { value: 'pulse', label: 'Pulse' },
  { value: 'bounce', label: 'Bounce' },
  { value: 'float', label: 'Float' },
  { value: 'shake', label: 'Shake' },
  { value: 'swing', label: 'Swing' },
  { value: 'glowPulse', label: 'Glow Pulse' },
];

export const TEXT_ANIMATIONS = [
  { value: 'none', label: 'None' },
  { value: 'fadeIn', label: 'Fade In' },
  { value: 'fadeInUp', label: 'Fade In Up' },
  { value: 'textReveal', label: 'Text Reveal' },
  { value: 'letterSpacing', label: 'Letter Expand' },
  { value: 'blurIn', label: 'Blur In' },
  { value: 'bounceIn', label: 'Bounce In' },
];

export const ANIMATION_SPEEDS = [
  { value: 'slow', label: 'Slow (1.5s)' },
  { value: 'normal', label: 'Normal (0.8s)' },
  { value: 'fast', label: 'Fast (0.4s)' },
];

export const ANIMATION_DELAYS = [
  { value: '0', label: 'No delay' },
  { value: '100', label: '0.1s' },
  { value: '200', label: '0.2s' },
  { value: '300', label: '0.3s' },
  { value: '500', label: '0.5s' },
  { value: '700', label: '0.7s' },
  { value: '1000', label: '1s' },
];

const AnimationControls = ({ animation = {}, onChange, showTextAnimations = false }) => {
  const {
    entrance = 'none',
    attention = 'none',
    textAnimation = 'none',
    speed = 'normal',
    delay = '0',
    animateOnScroll = false,
  } = animation;

  const updateAnimation = (key, value) => {
    onChange({ ...animation, [key]: value });
  };

  return (
    <div className="space-y-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
      <div className="flex items-center gap-2 text-sm font-semibold text-slate-700 pb-2 border-b border-slate-200">
        <Sparkles size={16} className="text-purple-500" />
        Animation
      </div>

      {/* Entrance Animation */}
      <div className="space-y-2">
        <Label className="text-xs text-slate-600 flex items-center gap-1">
          <Play size={12} />
          Entrance Animation
        </Label>
        <Select value={entrance} onValueChange={(v) => updateAnimation('entrance', v)}>
          <SelectTrigger className="h-9">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {ENTRANCE_ANIMATIONS.map(anim => (
              <SelectItem key={anim.value} value={anim.value}>{anim.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Text Animation (for hero blocks) */}
      {showTextAnimations && (
        <div className="space-y-2">
          <Label className="text-xs text-slate-600">Text Animation</Label>
          <Select value={textAnimation} onValueChange={(v) => updateAnimation('textAnimation', v)}>
            <SelectTrigger className="h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TEXT_ANIMATIONS.map(anim => (
                <SelectItem key={anim.value} value={anim.value}>{anim.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Attention Animation (loop) */}
      <div className="space-y-2">
        <Label className="text-xs text-slate-600 flex items-center gap-1">
          <Zap size={12} />
          Continuous Effect
        </Label>
        <Select value={attention} onValueChange={(v) => updateAnimation('attention', v)}>
          <SelectTrigger className="h-9">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {ATTENTION_ANIMATIONS.map(anim => (
              <SelectItem key={anim.value} value={anim.value}>{anim.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Speed & Delay Row */}
      {(entrance !== 'none' || attention !== 'none' || textAnimation !== 'none') && (
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label className="text-xs text-slate-600">Speed</Label>
            <Select value={speed} onValueChange={(v) => updateAnimation('speed', v)}>
              <SelectTrigger className="h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ANIMATION_SPEEDS.map(s => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label className="text-xs text-slate-600 flex items-center gap-1">
              <Clock size={12} />
              Delay
            </Label>
            <Select value={delay} onValueChange={(v) => updateAnimation('delay', v)}>
              <SelectTrigger className="h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ANIMATION_DELAYS.map(d => (
                  <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      {/* Animate on Scroll */}
      {entrance !== 'none' && (
        <div className="flex items-center justify-between pt-2 border-t border-slate-200">
          <Label className="text-xs text-slate-600">Animate when scrolled into view</Label>
          <Switch
            checked={animateOnScroll}
            onCheckedChange={(checked) => updateAnimation('animateOnScroll', checked)}
          />
        </div>
      )}
    </div>
  );
};

export default AnimationControls;

// Helper function to get animation classes
export const getAnimationClasses = (animation = {}) => {
  const {
    entrance = 'none',
    attention = 'none',
    speed = 'normal',
    delay = '0',
    animateOnScroll = false,
  } = animation;

  const classes = ['animate'];

  if (entrance !== 'none') {
    classes.push(`animate-${entrance}`);
  }

  if (attention !== 'none') {
    classes.push(`animate-${attention}`);
  }

  classes.push(`animate-${speed}`);

  if (delay !== '0') {
    classes.push(`animate-delay-${delay}`);
  }

  if (animateOnScroll) {
    classes.push('animate-on-scroll');
  }

  return classes.join(' ');
};
